import { AuthHelpCameFrom, CountryGroup } from '@/utils/enums';
import { ReferralEventDataObj } from '@/utils/referral.eventparams';
import { trackGaEvent, trackMoengageEvent } from '.';

export enum EventType {
  LOGIN_CLICK = 'login_click',
  HOMEPAGE_VISIT = 'homepage_visit',
  COUNTRY_CHANGE = 'country_change',
  BOOK_A_DEMO_CLICK = 'book_a_demo_click',
  COHORT_CHANGE = 'cohort_change',
  OTP_FAILED = 'otp_failed',
  DEMO_ENROLL_CLICK = 'demo_enroll_click',
  HELP_SUBMIT_CLICK = 'help_submit_click',
  HELP_CLICK = 'help_click',
  DEMO_BOOK_SEAT = 'demo_book_seat',
  DEMO_PAGE_VISIT = 'demo_page_visit',
  CLOSE_POPUP = 'close_popup',
  PAGE_OPEN = 'page_open',
  REFERRAL_MLP_CLAIM_NOW_CLICKED = 'referral_mlp_claim_now_clicked',
  OPEN_SESSION_CLICK = 'open_session_click',
}

export type OpenSessionClickFrom = 'home_page' | 'explore_page' | 'learn_and_play_page';

export type OpenSessionClickType = 'video_preview' | 'watch_free_cta';

export interface OpenSessionClickParams {
  from: OpenSessionClickFrom;
  batch_name?: string;
  topic_name?: string;
  open_session_link?: string;
  schedule_id?: string;
  click_type: OpenSessionClickType;
  experiment_name?: string;
}

export const eventTracker = {
  loginClick: (eventParams: { from: string; batch_type?: string }) => {
    trackGaEvent({
      eventName: EventType.LOGIN_CLICK,
      eventParams,
    });
  },
  homePageVisit: (eventParams: { came_from: string }) => {
    trackGaEvent({
      eventName: EventType.HOMEPAGE_VISIT,
      eventParams,
    });
  },
  countryChange: (eventParams: {
    click_section: string;
    page: string;
    prev_country: string;
    new_country: string;
  }) => {
    trackGaEvent({
      eventName: EventType.COUNTRY_CHANGE,
      eventParams,
    });
  },
  bookADemoClick: async (eventParams: {
    clickFrom?: 'sticky' | 'hero_section' | 'cohorts_section' | 'product_features_section';
    selected_cohort?: string;
    came_from?: string;
    page_link?: string;
    referral_mlp?: string;
    referrer_code?: string;
    referrer_name?: string;
  }) => {
    const referralParams = await ReferralEventDataObj.getReferralEventData();
    if (referralParams) {
      eventParams = {
        ...eventParams,
        ...referralParams,
      };
    }
    trackGaEvent({
      eventName: EventType.BOOK_A_DEMO_CLICK,
      eventParams: { ...eventParams, came_from: eventParams?.came_from || 'landing_page' },
    });
  },
  cohortChange: (eventParams: {
    came_from: 'dropdown' | 'profile' | 'landing_page';
    prev_cohort: string;
    new_cohort: string;
    page?: 'home' | 'landing' | 'paid';
    referrer_name?: string;
    referrer_code?: string;
    source?: string;
    flow?: string;
  }) => {
    const enhancedParams = {
      ...eventParams,
      source: eventParams.source || 'web',
      flow: eventParams.flow || 'revamped',
      URL: typeof window !== 'undefined' ? window.location.href : '',
    };
    trackGaEvent({
      eventName: EventType.COHORT_CHANGE,
      eventParams: {
        ...enhancedParams,
        is_user_status_required: true,
      },
    });
    trackMoengageEvent({
      eventName: EventType.COHORT_CHANGE,
      eventParams: {
        ...enhancedParams,
        is_user_status_required: true,
      },
    });
  },
  otpFailed: (eventParams: { phoneNumber: string; error: any; type: 'get-otp' | 'register' }) => {
    trackGaEvent({
      eventName: EventType.OTP_FAILED,
      eventParams,
    });
  },
  demoEnrollClick: (eventParams: {
    page_id?: string;
    page_link: string;
    default_amount: number;
  }) => {
    trackGaEvent({
      eventName: EventType.DEMO_ENROLL_CLICK,
      eventParams,
    });
  },
  helpSubmitClick: (eventParams: {
    user_number: string;
    user_country_code: string;
    came_from: AuthHelpCameFrom;
    user_name: string;
    user_email: string;
  }) => {
    trackGaEvent({
      eventName: EventType.HELP_SUBMIT_CLICK,
      eventParams,
    });
  },
  helpClick: (eventParams: {
    user_number: string;
    user_country_code: string;
    came_from: AuthHelpCameFrom;
  }) => {
    trackGaEvent({
      eventName: EventType.HELP_CLICK,
      eventParams,
    });
  },
  demoBookSeat: (eventParams: {
    page_id?: string;
    user_class: string;
    amount: number;
    batch_id: string;
    page: 'landing';
  }) => {
    trackGaEvent({
      eventName: EventType.DEMO_BOOK_SEAT,
      eventParams,
    });
  },
  demoPageVisit: async (eventParams: {
    page_id: string;
    page_link: string;
    came_from?: string;
  }) => {
    const referralParams = await ReferralEventDataObj.getReferralEventData();
    if (referralParams) {
      eventParams = {
        ...eventParams,
        ...referralParams,
      };
    }
    trackGaEvent({
      eventName: EventType.DEMO_PAGE_VISIT,
      eventParams,
    });
  },
  close_popup: () => {
    trackGaEvent({
      eventName: EventType.CLOSE_POPUP,
    });
  },
  pageOpen: (eventParams: {
    page: 'landing';
    paid_batch_name: [];
    paid_batch_id: [];
    country: CountryGroup;
    source?: string;
    flow?: string;
  }) => {
    trackGaEvent({
      eventName: EventType.PAGE_OPEN,
      eventParams: {
        ...eventParams,
        source: eventParams.source || 'web',
        flow: eventParams.flow || 'revamped',
      },
    });
    trackMoengageEvent({
      eventName: 'home_page_open',
      eventParams: {
        ...eventParams,
        source: eventParams.source || 'web',
        flow: eventParams.flow || 'revamped',
        is_user_status_required: true,
      },
    });
  },
  referralMlpClaimNowClicked: async (eventParams: {
    country: string;
    referrer_name: string;
    referrer_code: string;
    page_url: string;
    page_id: string;
  }) => {
    const referralParams = await ReferralEventDataObj.getReferralEventData();
    if (referralParams) {
      eventParams = {
        ...eventParams,
        ...referralParams,
      };
    }
    eventParams.page_url = window.location.href;
    trackGaEvent({
      eventName: EventType.REFERRAL_MLP_CLAIM_NOW_CLICKED,
      eventParams,
    });
  },
  storeClick: (eventParams: {
    journey?: string;
    source?: string;
    platform?: string;
    screen?: string;
    grade?: string;
  }) => {
    trackGaEvent({
      eventName: 'store_click',
      eventParams,
    });
    trackMoengageEvent({
      eventName: 'store_click',
      eventParams,
    });
  },
  storeLinkClick: (eventParams: {
    journey?: string;
    source?: string;
    platform?: string;
    screen?: string;
    grade?: string;
  }) => {
    trackGaEvent({
      eventName: 'bottom_tab_click',
      eventParams,
    });
  },

  openSessionClick: (eventParams: OpenSessionClickParams) => {
    trackGaEvent({
      eventName: EventType.OPEN_SESSION_CLICK,
      eventParams,
    });
    trackMoengageEvent({
      eventName: EventType.OPEN_SESSION_CLICK,
      eventParams,
    });
  },
};
